import Axios from "axios";
import { getApiBaseUrl, getAuthScheme } from "@/config/runtime";

function normalizeAxiosError(error) {
  const responseData = error?.response?.data;
  const status = error?.response?.status;
  const method = error?.config?.method?.toUpperCase?.() || "REQUEST";
  const url = error?.config?.url || "";
  const message =
    responseData?.detail ||
    responseData?.message ||
    error?.message ||
    "Request failed";
  return {
    ...responseData,
    status,
    method,
    url,
    message,
  };
}

function createAxiosConnector({ isFormData = false } = {}) {
  const instance = Axios.create({
    baseURL: getApiBaseUrl(),
    ...(isFormData
      ? {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
      : {}),
  });
  instance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(normalizeAxiosError(error))
  );
  return instance;
}

class ApiConnector {
  connector;
  connectorFormData;
  refreshHandler;
  unauthorizedHandler;
  refreshPromise;
  constructor() {
    this.refreshHandler = null;
    this.unauthorizedHandler = null;
    this.refreshPromise = null;
    this.connector = createAxiosConnector();
    this.connectorFormData = createAxiosConnector({ isFormData: true });
    this.unauthenticatedConnector = createAxiosConnector();
  }
  setAuthHeaders(token) {
    const authScheme = getAuthScheme();
    this.connector.defaults.headers.common.Authorization = `${authScheme} ${token}`;
    this.connectorFormData.defaults.headers.common.Authorization = `${authScheme} ${token}`;
  }
  clearAuthHeaders() {
    delete this.connector.defaults.headers.common["Authorization"];
    delete this.connectorFormData.defaults.headers.common["Authorization"];
  }

  setRefreshHandler(handler) {
    this.refreshHandler = handler;
  }

  setUnauthorizedHandler(handler) {
    this.unauthorizedHandler = handler;
  }

  isTokenExpiredError(error) {
    return error?.status === 401 && error?.code === "token_not_valid";
  }

  isUnauthorizedError(error) {
    return error?.status === 401;
  }

  triggerUnauthorized() {
    if (this.unauthorizedHandler) {
      try {
        this.unauthorizedHandler();
      } catch (e) {
        // ignore
      }
    }
  }

  async tryRefreshToken() {
    if (!this.refreshHandler) {
      return false;
    }
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    this.refreshPromise = Promise.resolve(this.refreshHandler())
      .then((result) => Boolean(result))
      .catch(() => false)
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }

  async requestWithFallback(requestFn, fallbackFn, options = {}) {
    const { skipAuthRefresh = false, skipUnauthorizedHandler = false } =
      options;
    try {
      const response = await requestFn();
      return response?.data ?? response;
    } catch (error) {
      if (!skipAuthRefresh && this.isTokenExpiredError(error) && !String(error?.url || "").includes("auth/refresh/")) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          try {
            const retryResponse = await requestFn();
            return retryResponse?.data ?? retryResponse;
          } catch (retryError) {
            if (
              !skipUnauthorizedHandler &&
              this.isUnauthorizedError(retryError)
            ) {
              this.triggerUnauthorized();
            }
            throw retryError;
          }
        }
      }
      if (
        !skipUnauthorizedHandler &&
        this.isUnauthorizedError(error) &&
        !String(error?.url || "").includes("auth/refresh/")
      ) {
        this.triggerUnauthorized();
      }
      throw error;
    }
  }
}

export default new ApiConnector();
